import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  updateProfile
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';

// Initialize Google provider
const googleProvider = new GoogleAuthProvider();

// ============================================
// 🔒 WHITELIST - Only these emails can access
// ============================================
const ALLOWED_EMAILS = [
  'ifarrisbusiness@gmail.com',      // Isaac
  'sandersonolivia3@gmail.com',     // Wife
];

// Set to false when ready to launch publicly
const WHITELIST_ENABLED = true;

// Helper function to check if email is allowed
const isEmailAllowed = (email) => {
  if (!WHITELIST_ENABLED) return true;
  if (!email) return false;
  return ALLOWED_EMAILS.includes(email.toLowerCase());
};
// ============================================

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Sign up new user
  const signup = async (email, password, displayName, userType = 'sitter') => {
    // 🔒 Whitelist check
    if (!isEmailAllowed(email)) {
      throw new Error('ACCESS_RESTRICTED');
    }
    
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Update display name
      await updateProfile(user, { displayName });

      // Create user profile in Firestore
      const userDocRef = doc(db, 'users', user.uid);
      const profileData = {
        uid: user.uid,
        email: user.email,
        displayName: displayName,
        userType: userType, // 'sitter' or 'client'
        createdAt: new Date().toISOString(),
        theme: 'pink', // Default theme
        businessName: userType === 'sitter' ? '' : null,
      };

      await setDoc(userDocRef, profileData);
      setUserProfile(profileData);

      return user;
    } catch (error) {
      console.error('Signup error:', error);
      throw error;
    }
  };

  // Sign in existing user
  const login = async (email, password) => {
    // 🔒 Whitelist check
    if (!isEmailAllowed(email)) {
      throw new Error('ACCESS_RESTRICTED');
    }
    
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      return userCredential.user;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  // Sign in with Google
  const loginWithGoogle = async (userType = 'sitter') => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;

      // 🔒 Whitelist check for Google login
      if (!isEmailAllowed(user.email)) {
        // Sign them out immediately and silently
        await signOut(auth);
        // Clear any state
        setCurrentUser(null);
        setUserProfile(null);
        throw new Error('ACCESS_RESTRICTED');
      }

      // Check if user profile exists
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);

      // If new user, create profile
      if (!userDoc.exists()) {
        const profileData = {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          userType: userType,
          createdAt: new Date().toISOString(),
          theme: 'pink',
          businessName: userType === 'sitter' ? '' : null,
          photoURL: user.photoURL,
        };
        await setDoc(userDocRef, profileData);
        setUserProfile(profileData);
      }

      return user;
    } catch (error) {
      // Re-throw ACCESS_RESTRICTED errors
      if (error.message === 'ACCESS_RESTRICTED') {
        throw error;
      }
      console.error('Google login error:', error);
      throw error;
    }
  };

  // Sign out
  const logout = async () => {
    try {
      await signOut(auth);
      setUserProfile(null);
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  };

  // Fetch user profile from Firestore
  const fetchUserProfile = async (uid) => {
    try {
      const userDocRef = doc(db, 'users', uid);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        const profile = userDoc.data();
        setUserProfile(profile);
        return profile;
      }
      return null;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      return null;
    }
  };

  // Listen to auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      // 🔒 Check whitelist on auth state change too
      if (user && !isEmailAllowed(user.email)) {
        // Silently sign out unauthorized users
        await signOut(auth);
        setCurrentUser(null);
        setUserProfile(null);
        setLoading(false);
        return;
      }
      
      setCurrentUser(user);
      
      if (user) {
        await fetchUserProfile(user.uid);
      } else {
        setUserProfile(null);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userProfile,
    signup,
    login,
    loginWithGoogle,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
